import type { VisualizationSpec } from "vega-embed";
import type { CanvasItemFullApi, FieldRefApi } from "./storyCanvas.types";
import { MOCK_CHART_DRILL_HIERARCHIES, mockChartDrillDown } from "./chartDrillDown.mock";
import type { ChartDrillHierarchy } from "./crossFilters.types";
import type { BaselineFilterApi, DrillStateApi, FilterScalarApi, StoryFilterApi } from "./storyFilters.types";

/**
 * What the item GET adds for the filters saved on the story: which filters the
 * item's rows were narrowed by, which it can't reach, and which baseline a
 * story filter replaced. The mock stands in for the backend's query engine, so
 * "reaches a field" means the field is a column of the item's rows.
 */

type SpecObject = Record<string, unknown>;
type Row = Record<string, unknown>;

const STORY_WIDE_KINDS: ReadonlySet<StoryFilterApi["kind"]> = new Set(["global", "cross"]);

const isStoryWide = (filter: StoryFilterApi): boolean => STORY_WIDE_KINDS.has(filter.kind);

const baselineItemPrefix = (itemId: string) => `baseline:${itemId}:`;

const specRows = (spec: VisualizationSpec | null | undefined): Row[] => {
  const values = ((spec as SpecObject | null | undefined)?.data as SpecObject | undefined)?.values;
  return Array.isArray(values) ? (values as Row[]) : [];
};

const withRows = (spec: VisualizationSpec, rows: Row[]): VisualizationSpec =>
  ({ ...(spec as SpecObject), data: { values: rows } }) as VisualizationSpec;

const asList = (value: StoryFilterApi["value"]): FilterScalarApi[] =>
  Array.isArray(value) ? value : [value];

/** Only the comparators a categorical field can take narrow rows; the rest pass through. */
const keepsRow = (row: Row, filter: StoryFilterApi): boolean => {
  const cell = row[filter.field.model_field_id] as FilterScalarApi;
  const values = asList(filter.value);
  switch (filter.comparator) {
    case "eq":
    case "in":
      return values.includes(cell);
    case "neq":
    case "not_in":
      return !values.includes(cell);
    default:
      return true;
  }
};

const toBaselineFilter = (filter: StoryFilterApi, itemId: string): BaselineFilterApi => ({
  id: filter.id.replace(baselineItemPrefix(itemId), ""),
  field: filter.field,
  comparator: filter.comparator,
  value: filter.value,
});

const axisFieldOf = (item: CanvasItemFullApi): FieldRefApi | null => item.binding?.x?.field ?? null;

const axisColumnOf = (item: CanvasItemFullApi): string | null => {
  const x = ((item.spec as SpecObject | null | undefined)?.encoding as SpecObject | undefined)?.x as
    | SpecObject
    | undefined;
  return typeof x?.field === "string" ? x.field : null;
};

const levelRef = (field: string): FieldRefApi => ({ kind: "dimension_attribute", model_field_id: field });

const hierarchyOf = (axisFieldId: string | undefined): ChartDrillHierarchy | undefined =>
  MOCK_CHART_DRILL_HIERARCHIES.find((hierarchy) =>
    hierarchy.levels.some((level) => level.field === axisFieldId),
  );

/**
 * A chart's drill position from its `drill_down` rows, in the order taken. The
 * chart starts at the level its binding's x field sits on; each row moves it one
 * level down. Null for a chart whose axis is in no hierarchy.
 *
 * `rootField` stands in for the (not-yet-backend-supported) drill-down field
 * picker: when given, the chart is treated as drilling into *that* field's
 * hierarchy at its own level instead of the axis-derived one. Simplification:
 * only drill rows already recorded against this hierarchy count toward the
 * current level — a prior drill chain in a different hierarchy is ignored
 * rather than mixed in, since the mock doesn't simulate the chart's axis
 * actually rebinding to the newly picked hierarchy.
 */
export const toMockDrillState = (
  item: CanvasItemFullApi,
  drillRows: StoryFilterApi[],
  rootField?: FieldRefApi,
): DrillStateApi | null => {
  const rootId = rootField?.model_field_id ?? item.binding?.x?.field.model_field_id;
  const hierarchy = hierarchyOf(rootId);
  if (!hierarchy) return null;
  const levels = hierarchy.levels.map((level, index) => ({
    level_no: index + 1,
    field: levelRef(level.field),
    name: level.label,
  }));
  const startLevel = levels.find((level) => level.field.model_field_id === rootId)?.level_no ?? 1;
  const relevantDrillRows = rootField ? drillRows.filter((row) => row.hierarchy_id === hierarchy.id) : drillRows;
  const current = startLevel + relevantDrillRows.length;
  return {
    hierarchy_id: hierarchy.id,
    hierarchy_name: hierarchy.label,
    levels,
    current_level_no: current,
    can_drill_down: current < levels.length,
    path: relevantDrillRows.map((row, index) => ({
      level_no: startLevel + index,
      filter_id: row.id,
      value: Array.isArray(row.value) ? row.value[0] : row.value,
    })),
  };
};

/** Re-queries the chart one level down per drill step; the spec's x channel moves to the new level. */
const drilledSpec = (spec: VisualizationSpec, drillRows: StoryFilterApi[], chartId: string): VisualizationSpec =>
  mockChartDrillDown(
    {
      storyId: "",
      chartId,
      scopes: drillRows.map((row) => ({
        field: row.field.model_field_id,
        value: Array.isArray(row.value) ? row.value[0] : row.value,
      })),
    },
    spec,
  ).spec;

/** Legacy items take no story filters, so they render as they always did, greyed. */
const toLegacyItem = (item: CanvasItemFullApi): CanvasItemFullApi => ({
  ...item,
  filterable: false,
  filters_applied: [],
  filters_not_applicable: [],
  filters_overridden: [],
  axis_field: null,
  axis_column: null,
  drill: null,
});

export const applyStoryFiltersToItem = (
  item: CanvasItemFullApi,
  filters: StoryFilterApi[],
): CanvasItemFullApi => {
  if (!item.query_model) return toLegacyItem(item);

  const rows = specRows(item.spec);
  const columns = new Set(rows.flatMap((row) => Object.keys(row)));
  const baselines = filters.filter(
    (filter) => filter.kind === "baseline" && filter.canvas_item_id === item.id,
  );
  const ownRows = filters.filter((filter) => filter.kind === "local" && filter.canvas_item_id === item.id);
  const drillRows = filters
    .filter((filter) => filter.kind === "drill_down" && filter.canvas_item_id === item.id)
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const storyWide = filters.filter(isStoryWide);

  const reaches = (filter: StoryFilterApi) => columns.has(filter.field.model_field_id);
  const reached = storyWide.filter(reaches);
  const overridden = baselines.filter((baseline) =>
    reached.some((filter) => filter.field.model_field_id === baseline.field.model_field_id),
  );
  const applied = [
    ...reached,
    ...baselines.filter((baseline) => !overridden.includes(baseline)),
    ...ownRows,
    ...drillRows,
  ];

  // A drill step is not a row filter: the drill re-queries the chart one level down.
  const narrowing = applied.filter(
    (filter) => filter.kind !== "drill_down" && columns.has(filter.field.model_field_id),
  );
  const narrowed = rows.filter((row) => narrowing.every((filter) => keepsRow(row, filter)));

  const narrowedSpec = item.spec ? withRows(item.spec, narrowed) : item.spec;
  const spec =
    narrowedSpec && drillRows.length ? drilledSpec(narrowedSpec, drillRows, item.id) : narrowedSpec;
  const drill = toMockDrillState(item, drillRows);
  const currentLevel = drill?.levels.find((level) => level.level_no === drill.current_level_no);

  return {
    ...item,
    query_model: {
      ...item.query_model,
      baseline_filters: baselines.map((filter) => toBaselineFilter(filter, item.id)),
    },
    spec,
    filterable: true,
    filters_applied: applied.map((filter) => filter.id),
    filters_not_applicable: storyWide.filter((filter) => !reaches(filter)).map((filter) => filter.id),
    filters_overridden: overridden.map((filter) => filter.id),
    axis_field: currentLevel?.field ?? axisFieldOf(item),
    axis_column: axisColumnOf({ ...item, spec }),
    drill,
  };
};
